<template>
  <div class="student-cloze-page">
    <shared-header
      ref="sharedHeader"
      @on-bound-student="handleOnBoundStudent()"
    />
    <bound-student-list-drawer
      ref="boundStudentListDrawer"
      @on-bound-student-click="handleOnBoundStudentClick"
    />

    <div class="student-cloze-list">
      <el-card class="main-card" shadow="hover">
        <template #header>
          <span class="card-title">Blank HW</span>
          <p class="card-desc">{{ pageDesc }}</p>
        </template>

        <div v-loading="listLoading" class="table-wrap">
          <el-table
            :data="assignmentList"
            stripe
            border
            highlight-current-row
            empty-text="暂无完形填空作业"
            style="width: 100%"
            @row-click="onRowClick"
          >
            <el-table-column prop="subjectName" label="科目" min-width="120" show-overflow-tooltip />
            <el-table-column prop="setName" label="词表" min-width="140" show-overflow-tooltip />
            <el-table-column label="类型" width="110" align="center">
              <template #default="{ row }">{{ row.type || '完形填空' }}</template>
            </el-table-column>
            <el-table-column label="状态" width="110" align="center">
              <template #default="{ row }">
                <el-tag :type="statusTagType(row)" size="small">
                  {{ statusLabel(row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="成绩" width="110" align="center">
              <template #default="{ row }">
                <template v-if="row.totalQuestions">
                  {{ row.totalCorrect }} / {{ row.totalQuestions }}
                </template>
                <template v-else>—</template>
              </template>
            </el-table-column>
            <el-table-column label="正确率" width="90" align="center">
              <template #default="{ row }">
                {{ row.accuracyPercent != null ? row.accuracyPercent + '%' : '—' }}
              </template>
            </el-table-column>
            <el-table-column label="布置时间" width="160">
              <template #default="{ row }">
                {{ formatClozeDateTime(row.createdDate) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import SharedHeader from '../../shared/SharedHeader.vue'
import BoundStudentListDrawer from '../../admin/BoundStudentListDrawer.vue'
import { ElMessage } from 'element-plus'
import { formatClozeDateTime, COMPLETION_TYPE, isCompletionType } from './completionUtils'

export default {
  name: 'CompletionAssignmentList',
  components: { SharedHeader, BoundStudentListDrawer },
  data () {
    return {
      assignmentList: [],
      listLoading: false,
      userRole: localStorage.getItem('userRole') || ''
    }
  },
  computed: {
    isStudent () {
      return this.userRole === 'Student'
    },
    pageDesc () {
      return this.isStudent
        ? '你被布置的完形填空作业。点击一行进入练习。'
        : '当前绑定学生的完形填空作业。已完成的作业可点击查看结果。'
    }
  },
  mounted () {
    this.loadList()
  },
  methods: {
    formatClozeDateTime,

    resolveStudentId () {
      const raw = this.isStudent
        ? localStorage.getItem('userId')
        : localStorage.getItem('targetId') || localStorage.getItem('userId')
      const studentId = Number(raw)
      return Number.isFinite(studentId) && studentId > 0 ? studentId : null
    },

    assignmentIdOf (row) {
      return Number(row?.assignmentId || row?.id) || 0
    },

    isFinished (row) {
      return row?.status === 'finished' || Number(row?.resultId) > 0
    },

    statusLabel (row) {
      return this.isFinished(row) ? '已完成' : '未完成'
    },

    statusTagType (row) {
      if (this.isFinished(row)) return 'success'
      if (row?.status === 'pending') return 'warning'
      return 'info'
    },

    loadList () {
      const studentId = this.resolveStudentId()
      if (!studentId) {
        this.assignmentList = []
        ElMessage.warning(
          this.isStudent ? '未找到学生 ID' : '请先绑定学生后再查看作业'
        )
        return
      }
      this.listLoading = true
      const params = { studentId, type: COMPLETION_TYPE }
      Promise.all([
        this.$store.dispatch('VocClozeAssignment/getOverview', params).catch(() => ({ data: [] })),
        this.$store.dispatch('VocClozeAssignment/getByStudentId', params).catch(() => ({ data: [] }))
      ])
        .then(([overviewRes, assignmentRes]) => {
          const overviews = Array.isArray(overviewRes?.data) ? overviewRes.data : []
          const assignments = Array.isArray(assignmentRes?.data) ? assignmentRes.data : []
          const typeById = new Map(
            assignments.map((item) => [Number(item.id), item.type])
          )
          const source = overviews.length
            ? overviews
            : assignments.map((item) => ({
              ...item,
              assignmentId: item.id
            }))
          this.assignmentList = this.sortList(
            source
              .map((item) => ({
                ...item,
                type: item.type || typeById.get(this.assignmentIdOf(item)) || COMPLETION_TYPE
              }))
              .filter(isCompletionType)
          )
        })
        .catch((err) => {
          console.error(err)
          this.assignmentList = []
          const d = err.response?.data
          const msg =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载完形填空作业失败'
          ElMessage.error(String(msg))
        })
        .finally(() => {
          this.listLoading = false
        })
    },

    sortList (list) {
      return [...list].sort((a, b) => {
        const da = new Date(a.createdDate || 0).getTime()
        const db = new Date(b.createdDate || 0).getTime()
        return db - da
      })
    },

    onRowClick (row) {
      const assignmentId = this.assignmentIdOf(row)
      if (!assignmentId) return

      if (this.isStudent) {
        if (this.isFinished(row) && Number(row.resultId) > 0) {
          this.$router.push({
            path: '/studentblankresult',
            query: { assignmentResultId: String(row.resultId) }
          })
          return
        }
        this.$router.push({
          path: '/wordBlank',
          query: { assignmentId: String(assignmentId) }
        })
        return
      }

      if (Number(row.resultId) > 0) {
        this.$router.push({
          path: '/studentblankresult',
          query: { assignmentResultId: String(row.resultId) }
        })
        return
      }
      ElMessage.info('学生尚未完成该作业')
    },

    handleOnBoundStudent () {
      this.$refs.boundStudentListDrawer.openDrawer()
    },

    handleOnBoundStudentClick () {
      this.$refs.sharedHeader.updateBoundStudentChineseName()
      this.loadList()
    }
  }
}
</script>

<style scoped lang="scss">
.student-cloze-page {
  text-align: left;
}

.student-cloze-list {
  max-width: 1100px;
  margin: 24px auto;
  padding: 0 16px;
}

.main-card {
  border-radius: 8px;
}

.card-title {
  font-size: 1.15rem;
  font-weight: 600;
}

.card-desc {
  margin: 0.35rem 0 0;
  color: #888;
  font-size: 0.9rem;
}

.table-wrap {
  min-height: 120px;
}

:deep(.el-table__row) {
  cursor: pointer;
}
</style>
